import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isHome = location.pathname === "/";
  const isTransparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const linkBase = "text-sm font-medium transition-colors whitespace-nowrap";
  const linkColor = isTransparent ? "text-white/85 hover:text-white" : "text-foreground-700 hover:text-primary-600";
  const activeColor = isTransparent ? "text-white" : "text-primary-600";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent ? "bg-transparent" : "bg-background-50/95 backdrop-blur border-b border-background-200"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center text-white">
              <i className="ri-play-fill text-xl"></i>
            </span>
            <span className={`font-heading font-bold text-base md:text-lg whitespace-nowrap ${isTransparent ? "text-white" : "text-foreground-950"}`}>
              高清视频免费观看
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) => `${linkBase} ${isActive ? activeColor : linkColor}`}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form onSubmit={submitSearch} className="hidden lg:flex items-center">
              <div className="relative">
                <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm ${isTransparent ? "text-white/70" : "text-foreground-400"}`}></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影片、剧集..."
                  className={`w-52 pl-9 pr-3 py-2 rounded-md text-sm outline-none transition-all focus:w-64 ${
                    isTransparent
                      ? "bg-white/15 text-white placeholder:text-white/60 focus:bg-white/25"
                      : "bg-background-100 text-foreground-900 placeholder:text-foreground-400 border border-background-300 focus:border-primary-400"
                  }`}
                />
              </div>
            </form>

            <Link
              to="/search"
              className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-md transition-colors ${
                isTransparent ? "text-white hover:bg-white/15" : "text-foreground-700 hover:bg-background-200"
              }`}
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md transition-colors ${
                isTransparent ? "text-white hover:bg-white/15" : "text-foreground-700 hover:bg-background-200"
              }`}
              aria-label="菜单"
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 animate-fade-in">
          <div className="px-4 py-3">
            <form onSubmit={submitSearch} className="relative mb-3">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片、剧集..."
                className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 border border-background-300 focus:border-primary-400 outline-none"
              />
            </form>
            <nav className="flex flex-col">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    `py-3 px-2 rounded-md text-sm font-medium ${
                      isActive ? "text-primary-600 bg-primary-50" : "text-foreground-700 hover:bg-background-100"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <NavLink to="/search" className="py-3 px-2 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100">
                搜索
              </NavLink>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}